import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';

import { jobsConfigApi, type TeamJob } from '@/api/queries/jobsConfig';
import { isTenantAdmin, useTenantStore } from '@/auth/tenantStore';
import { ErrorComponent } from '@/components/common/ErrorComponent';
import { useSetBreadcrumbs } from '@/components/layout/breadcrumbs';
import { Button, Icon, Table, Tag, type TableColumn } from '@/components/ui';

// Tenant-side mirror of the admin job configuration list (/admin/jobs/settings):
// one row per per-tenant job, opening /settings/jobs/$key for Settings + Run history.
function JobsListRoute() {
  useSetBreadcrumbs([{ label: 'Settings' }, { label: 'Jobs' }]);

  const navigate = useNavigate();
  const { data: jobs, isPending } = jobsConfigApi.useTenantJobs();

  const columns: TableColumn<TeamJob>[] = [
    {
      id: 'name',
      header: 'Job',
      cell: (j) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 500 }}>{j.displayName}</span>
          <span style={{ fontSize: 12, color: 'var(--text-3)', maxWidth: 480 }}>{j.description}</span>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (j) => (
        <Tag tone={j.enabledForTenant ? 'green' : 'default'}>
          {j.enabled === null || j.enabled === undefined
            ? `Inherit (${j.globalEnabled ? 'on' : 'off'})`
            : j.enabled
              ? 'On'
              : 'Off'}
        </Tag>
      ),
      width: 130,
    },
    {
      id: 'global',
      header: 'Global',
      cell: (j) => (
        <Tag tone={j.globalEnabled ? 'green' : 'amber'}>{j.globalEnabled ? 'On' : 'Off'}</Tag>
      ),
      width: 90,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-3)', maxWidth: 640 }}>
        Enable or disable these background jobs for your organisation and tune their behaviour. The
        schedule is managed globally by system administrators.
      </p>
      <Table
        columns={columns}
        rows={jobs ?? []}
        rowKey={(j) => j.key}
        loading={isPending}
        emptyMessage="No tenant-configurable jobs."
        onRowClick={(j) => navigate({ to: '/settings/jobs/$key', params: { key: j.key } })}
        rowActions={(j) => (
          <Button
            variant="secondary"
            leftIcon={<Icon name="settings" size={13} />}
            onClick={() => navigate({ to: '/settings/jobs/$key', params: { key: j.key } })}
          >
            Configure
          </Button>
        )}
      />
    </div>
  );
}

export const Route = createFileRoute('/settings/jobs/')({
  beforeLoad: () => {
    if (!isTenantAdmin(useTenantStore.getState())) {
      throw redirect({ to: '/contacts', search: { page: 0, pageSize: 25 } });
    }
  },
  component: JobsListRoute,
  errorComponent: ErrorComponent,
});
